import {
  Button,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import UploadIcon from '@mui/icons-material/Publish';
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { format } from "date-fns";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { cloneDeep } from "lodash";
import moment from "moment";
import PurchaseOrderProcessDialog from "../components/PurchaseOrderProcessDialog";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const PAGE_SIZE = 20;
const ROW_HEIGHT = 70;

const defaultFilter = {
  date: null,
};

export default function AGLCPurchaseOrders() {
  const appContext = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const [dialogVisible, setDialogVisible] = useState({ visible: false, initialStep: 0 });
  const [filter, setFilter] = useState(defaultFilter);

  const [products, setProducts] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [rowCount, setRowCount] = useState(0);

  const [recordId, setRecordId] = useState(null);

 const goToDetailView = async (id) => {
    try {
      const response = await fetchWithInterceptor(`/api/integrations/purchase-orders/${id}`, {
        headers: { "x-access-token": authContext.getToken() }
      });

      if (!response.ok) throw new Error("Failed to fetch order");

      const order = await response.json();

      let initialStep = 0;
      if (order.resultUrl) {
        initialStep = 3;
      } else if (order.confirmationUrl) {
        initialStep = 2;
      } else if (order.productsUrl) {
        initialStep = 2;
      }

      setRecordId(order.id);
      setDialogVisible({
        visible: true,
        initialStep,
        productsUrl: order.productsUrl,
        confirmationUrl: order.confirmationUrl,
        resultUrl: order.resultUrl
      });
    } catch (err) {
      console.error(err);
      appContext.showMessage("Error loading order", "error");
    }
  };

  const columns = [
    {
      field: "createdAt",
      headerName: "Created at",
      width: 250,
      sortable: false,
      renderCell: (params) => {
        const date = params?.row?.createdAt ? new Date(params.row.createdAt) : null;
        return (
          <Typography
            variant="body2"
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              textAlign: 'start',
            }}
          >
              {date ? format(date, "MM/dd/yyyy hh:mm a") : "—"}
          </Typography>
        );
      }
    },
    {
      field: "id",
      headerName: "Detail",
      width: 400,
      sortable: false,
      renderCell: (params) => {
        return (
          <Button color="primary" onClick={() => goToDetailView(params?.row?.id)}>View details</Button>
        );
      },
    },
  ];

  const showDialog = () => setDialogVisible({ visible: true, initialStep: 0 });

  const hideDialog = (reload) => {
    setDialogVisible({ visible: false, initialStep: 0 });
    if (reload) {
      setPaginationModel({ ...paginationModel, page: 0 });
      loadProducts(true);
    }
  }

  const handleReset = () => setFilter(cloneDeep(defaultFilter))

  let active = useRef(false);

  const loadProducts = useCallback(async (reset) => {
    setIsLoading(true);
    let filters = {
      limit: PAGE_SIZE,
      offset: reset ? 0 : paginationModel.page * PAGE_SIZE,
    };
    if (filter.date) {
      filters.dateFrom = moment.utc(filter.date).startOf('day').toISOString();
      filters.dateTo = moment.utc(filter.date).endOf('day').toISOString();
    }
    const params = new URLSearchParams(filters);
    const response = await fetchWithInterceptor(
      `/api/integrations/purchase-orders/?${params}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": authContext.getToken(),
        },
      }
    );
    if (response.ok) {
      if (!active.current) return;
        const data = await response.json();
      if (reset) {
          setPaginationModel({ ...paginationModel, page: 0 });
        }
        setProducts(data.orders);
      setRowCount(data.total);
    } else {
        const text = await response.text();
        console.error("Response not ok. Status:", response.status);
        console.error("Body:", text);
        appContext.showMessage("There was an error getting the products", "error");
      }
          setIsLoading(false);
        }, [filter, paginationModel, authContext, appContext]);

  useEffect(() => {
    active.current = true;

    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    loadProducts(false);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page]);

  useEffect(() => {
    loadProducts(true);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <>
      <Typography variant="body1" sx={{ ml: 3, mt: 3, mr: 5, mb: 1, color: "text.secondary" }}>
        Sync Cin7 Purchase Orders with AGLC records to ensure data consistency across both systems. Follow the on-screen instructions to process POs. Format: (sku, qty).
      </Typography>
      <Container component="main" maxWidth="100%" sx={{ pb: 3 }}>
        <Paper sx={{ width: "100%", bgcolor: "background.paper", mt: 2, mb: 2 }}>
          <div style={{ padding: 16 }}>
            <Typography variant="h6">Search Filters</Typography>
            <br />
            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
              <Grid sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', md: 'span 3' } }}>
                <DatePicker
                  label="Uploaded at"
                  value={filter.date ? new Date(filter.date) : null}
                  onChange={(date) => {
                    setFilter({ ...filter, date: date ? date.toISOString() : null });
                  }}
                  slotProps={{ textField: { variant: "outlined", fullWidth: true } }}
                />
              </Grid>
              <Grid sx={{ display: "flex", justifyContent: "flex-end", gridColumn: { xs: 'span 12', sm: 'span 6', md: 'auto' } }}>
                <Button onClick={handleReset}>
                  RESET FILTERS
                </Button>
              </Grid>
            </Grid>
          </div>
          <Divider />

          <Grid container justifyContent="flex-end" style={{ padding: 16 }} spacing={1}>
            <Grid>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={showDialog}
                startIcon={<UploadIcon />}
              >
                GENERATE PO
              </Button>
            </Grid>
          </Grid>

          <DataGrid
            paginationMode="server"
            rows={products}
            columns={columns}
            pageSizeOptions={[PAGE_SIZE]}
            rowsPerPageOptions={[PAGE_SIZE]}
            disableSelectionOnClick
            sx={{
              height: ROW_HEIGHT * PAGE_SIZE + 120,
              border: "none",
              "& .MuiDataGrid-menuIcon": {
                display: "none",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                textTransform: "uppercase",
                fontWeight: 600,
                fontSize: "0.75rem",
                color: "rgba(58, 53, 65, 0.87)",
                whiteSpace: "wrap",
                lineHeight: 1,
              },
              "& .MuiDataGrid-cell--withRenderer": {
                display: "block",
                textOverflow: "unset",
              },
              "& .MuiDataGrid-cell--withRenderer span": {
                overflow: "hidden",
                textWrap: "nowrap",
                textOverflow: "ellipsis",
                display: "block",
              },
              "& .MuiDataGrid-columnsContainer": {
                backgroundColor: "rgb(249, 250, 252)",
                justifyContent: "center",
              },
              "& .MuiChip-root.a_error": {
                color: "rgb(97, 26, 21)",
                backgroundColor: "rgb(253, 236, 234)",
              },
              "& .MuiChip-root.b_modified": {
                color: "rgb(30, 70, 32)",
                backgroundColor: "rgb(237, 247, 237)",
              },
              "& .MuiChip-root.c_unmodified": {
                color: "rgb(13, 60, 97)",
                backgroundColor: "rgb(232, 244, 253)",
              },
              // "& .MuiChip-root.d_unprocessed": {
              //   color: "rgb(13, 60, 97)",
              //   backgroundColor: "rgb(232, 244, 253)",
              // },
            }}
            disableColumnFilter
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={rowCount}
            loading={isLoading}
            rowHeight={ROW_HEIGHT}
          />
        </Paper>
        {dialogVisible.visible && (
          <PurchaseOrderProcessDialog
            open={dialogVisible.visible}
            onClose={hideDialog}
            confirmationOrderId={recordId}
            initialStep={dialogVisible.initialStep}
            initialProductsUrl={dialogVisible.productsUrl}
            initialConfirmationUrl={dialogVisible.confirmationUrl}
          />
        )}
      </Container>
    </>
  );
}
